import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Download, RefreshCw, Bus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BusCheckoutData } from '../interfaces/BusCheckout';
import { busCheckoutService } from '../services/busCheckoutService';
import { DefaultLoader } from '../components';
import { EmissionFilters, BusFilters } from '../components/EmissionFilters';
import styles from './Emissions.module.css';

const BusEmissions: React.FC = () => {
  const navigate = useNavigate();
  const [checkouts, setCheckouts] = useState<BusCheckoutData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<BusFilters>({});

const removeHyphens = (text: string | undefined) => text?.replace(/-/g, ' ') || '';

  const getDepartureBadge = (departureTime: string) => {
    const now = new Date();
    const departure = new Date(departureTime);
    const diffHours = (departure.getTime() - now.getTime()) / (1000 * 60 * 60);
    const diffDays = diffHours / 24;

    let color: string, bg: string, border: string;

    if (diffHours < 0) {
      color = 'var(--color-darkgray)'; bg = '#f0f0f0'; border = '#d0d0d0';
    } else if (diffHours <= 5) {
      color = 'var(--color-red)'; bg = '#fef2f2'; border = '#fecaca';
    } else if (diffDays <= 3) {
      color = '#b45309'; bg = '#fffbeb'; border = '#fde68a';
    } else {
      color = 'var(--color-green)'; bg = '#f0fdf4'; border = '#bbf7d0';
    }

    return (
      <div style={{
        padding: '2px 6px', borderRadius: '4px', fontSize: 'var(--font-size-11)',
        fontWeight: 600, color, backgroundColor: bg, border: `1px solid ${border}`, whiteSpace: 'nowrap',
        display: 'inline-block', marginTop: '2px',
      }}>
        {departure.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
        {' '}
        {departure.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </div>
    );
  };
  const loadCheckouts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await busCheckoutService.getBusCheckouts(
        currentPage,
        10,
        filters,
      );
      
      setCheckouts(response.data);
      setTotalPages(response.totalPages || Math.ceil(response.total / 10));
    } catch (err: any) {
      console.error('Erro ao carregar checkouts de ônibus:', err);
      setError('Erro ao carregar dados das emissões de ônibus');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    loadCheckouts();
  }, [loadCheckouts]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadCheckouts();
  };

  const formatCurrency = (value: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(parseFloat(value));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentStatusBadge = (paymentStatus?: string) => {
    if (!paymentStatus) return <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>—</span>;

    const config: Record<string, { label: string; color: string; bg: string; border: string }> = {
      aprovado:    { label: '✓ Aprovado',    color: 'var(--color-green)',    bg: '#f0fdf4', border: '#bbf7d0' },
      processando: { label: '⟳ Processando', color: '#b45309',               bg: '#fffbeb', border: '#fde68a' },
      reprovado:   { label: '✕ Reprovado',   color: 'var(--color-red)',      bg: '#fef2f2', border: '#fecaca' },
      reembolsado: { label: '↩ Reembolsado', color: 'var(--color-blue)',     bg: '#eff6ff', border: '#bfdbfe' },
    };

    const c = config[paymentStatus] ?? { label: paymentStatus, color: 'var(--color-darkgray)', bg: '#f1f5f9', border: '#cbd5e1' };

    return (
      <span style={{
        padding: '3px 8px', borderRadius: '4px', fontSize: 'var(--font-size-12)',
        fontWeight: 600, color: c.color, backgroundColor: c.bg, border: `1px solid ${c.border}`,
        whiteSpace: 'nowrap',
      }}>
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'awaiting payment': 'badge-processing',
      'processando': 'badge-pending',
      'completed': 'badge-completed',
      'cancelled': 'badge-cancelled',
      'failed': 'badge-failed',
    };

    const labelMap: { [key: string]: string } = {
      'awaiting payment': 'Aguardando Pagamento',
      'processando': 'Processando',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'failed': 'Falhou',
    };

    return (
      <span className={`badge ${statusMap[status] || 'badge-pending'}`}>
        {labelMap[status] || status}
      </span>
    );
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    loadCheckouts();
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  if (loading && checkouts.length === 0) {
    return <DefaultLoader title="Emissões de Ônibus" messages={['Carregando emissões...', 'Processando dados...', 'Organizando informações...']} />;
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h2>Emissão de Ônibus</h2>
          <p>Gerencie as emissões de passagens de ônibus</p>
        </div>
        <div className={styles.headerActions}>
          <button onClick={handleRefresh} className="btn btn-secondary" disabled={loading}>
            <RefreshCw size={16} /> Atualizar
          </button>
          <button className="btn btn-primary">
            <Download size={16} /> Exportar
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <EmissionFilters
          mode="bus"
          filters={filters}
          onChange={(f) => setFilters(f)}
          onApply={handleApplyFilters}
        />
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: 'var(--color-red)',
          padding: '12px 16px',
          borderRadius: 'var(--br-5)',
          marginBottom: '20px',
        }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className={styles.desktopTable}>
          <div className={styles.tableWrapper}>
            <table className="table">
          <thead>
            <tr>
              <th>Pedido ID</th>
              <th>Status</th>
              <th>Pagamento</th>
              <th>Passageiro</th>
              <th>Rota Ida</th>
              <th>Rota Volta</th>
              <th>Valor Total</th>
              <th>Data</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {checkouts.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center" style={{ padding: '40px' }}>
                  {loading ? 'Carregando...' : 'Nenhuma emissão encontrada'}
                </td>
              </tr>
            ) : (
              checkouts.map((checkout) => (
                <tr key={checkout.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-12)' }}>
                    {checkout.pedido_id}
                  </td>
                  <td>{getStatusBadge(checkout.status)}</td>
                  <td>{getPaymentStatusBadge(checkout.paymentStatus)}</td>
                  <td>
                    <div>
                      <div style={{ fontWeight: 500 }}>{checkout.name}</div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                        {checkout.email}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Bus size={14} />
                        {removeHyphens(checkout.cidade_origem_slug)} → {removeHyphens(checkout.cidade_destino_slug)}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                        {checkout.company_name} - {checkout.tipo_assento}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-11)' }}>
                        {getDepartureBadge(checkout.datetime_ida)}
                      </div>
                    </div>
                  </td>
                  <td>
                    {checkout.volta_trip_id ? (
                      <div>
                        <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Bus size={14} />
                          {removeHyphens(checkout.volta_cidade_origin_slug)} → {removeHyphens(checkout.volta_cidade_destino_slug)}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                          {checkout.volta_company_name} - {checkout.volta_tipo_assento}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-11)' }}>
                          {getDepartureBadge(checkout.volta_datetime_ida)}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>
                        Somente ida
                      </span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {formatCurrency(checkout.total_value || checkout.max_split_value)}
                  </td>
                  <td>{formatDate(checkout.created_at)}</td>
                  <td>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 8px' }}
                      onClick={() => navigate(`/bus-emissions/${checkout.id}`)}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className={styles.mobileCards}>
          {checkouts.length === 0 ? (
            <p className="text-center" style={{ padding: '40px' }}>
              {loading ? 'Carregando...' : 'Nenhuma emissão encontrada'}
            </p>
          ) : (
            checkouts.map((checkout) => (
              <div key={checkout.id} className={styles.mobileCard}>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Pedido</span>
                  <span className={styles.mobileCardValue} style={{ fontFamily: 'monospace' }}>
                    {checkout.pedido_id}
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Status</span>
                  <span className={styles.mobileCardValue}>{getStatusBadge(checkout.status)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Pagamento</span>
                  <span className={styles.mobileCardValue}>{getPaymentStatusBadge(checkout.paymentStatus)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Passageiro</span>
                  <span className={styles.mobileCardValue}>{checkout.name}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Ida</span>
                  <span className={styles.mobileCardValue}>
                    {removeHyphens(checkout.cidade_origem_slug)} → {removeHyphens(checkout.cidade_destino_slug)}
                    <br />
                    <small style={{ color: 'var(--color-darkgray)' }}>{checkout.company_name}</small>
                  </span>
                </div>
                {checkout.volta_trip_id && (
                  <div className={styles.mobileCardRow}>
                    <span className={styles.mobileCardLabel}>Volta</span>
                    <span className={styles.mobileCardValue}>
                      {removeHyphens(checkout.volta_cidade_origin_slug)} → {removeHyphens(checkout.volta_cidade_destino_slug)}
                      <br />
                      <small style={{ color: 'var(--color-darkgray)' }}>{checkout.volta_company_name}</small>
                    </span>
                  </div>
                )}
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Valor</span>
                  <span className={styles.mobileCardValue} style={{ fontWeight: 600 }}>
                    {formatCurrency(checkout.baseprice || checkout.max_split_value)}
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Data</span>
                  <span className={styles.mobileCardValue}>{formatDate(checkout.created_at)}</span>
                </div>
                <div className={styles.mobileCardActions}>
                  <button className="btn btn-secondary" style={{ padding: '6px 8px' }} onClick={() => navigate(`/bus-emissions/${checkout.id}`)}>
                    <Eye size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn btn-secondary" style={{ padding: '8px 12px' }}>
              Anterior
            </button>
            <span style={{ margin: '0 16px' }}>Página {currentPage} de {totalPages}</span>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="btn btn-secondary" style={{ padding: '8px 12px' }}>
              Próxima
            </button>
          </div>
        )}
      </div>

      <div className={styles.statsGrid}>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-orange)' }}>
            {checkouts.filter(c => c.status === 'awaiting payment').length}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Aguardando Pagamento</p>
        </div>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-blue)' }}>
            {checkouts.filter(c => c.status === 'processando').length}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Processando</p>
        </div>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-green)' }}>
            {checkouts.filter(c => c.status === 'completed').length}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Completados</p>
        </div>
      </div>
    </div>
  );
};

export default BusEmissions;
